import { TFunction } from "i18next";
import type { NotificationPayload, NotificationType } from "./notifications";

interface NotificationData {
  type: NotificationType;
  title: string;
  message: string;
  entity_type?: string | null;
  entity_id?: string | null;
  payload?: NotificationPayload | null;
}

interface ExtraData {
  entity_title?: string;
  severity?: string;
  status?: string;
  due_date?: string;
  days_overdue?: number;
  position_name?: string;
  is_first_reminder?: boolean;
  assignee_name?: string;
  commenter_name?: string;
  closed_by_name?: string;
  // TES-2080 (Typ 6, Mig 393): payload.role diskriminiert owner_restaffing-Notifications
  // ("items_reassignment" = N Items neu zuweisen vs. Owner-Vakanz).
  role?: string;
}

const severityPatterns = [
  { value: "low", pattern: /gering|^low$/ },
  { value: "medium", pattern: /mittel|^medium$/ },
  { value: "high", pattern: /hoch|^high$/ },
  { value: "critical", pattern: /kritisch|^critical$/ },
];

const statusPatterns = [
  { value: "open", pattern: /offen|^open$/ },
  { value: "in_progress", pattern: /bearbeitung|^in progress$/ },
  { value: "resolved", pattern: /behoben|^resolved$/ },
  { value: "closed", pattern: /geschlossen|^closed$/ },
];

const extractPatternValue = (
  text: string,
  patterns: Array<{ value: string; pattern: RegExp }>,
): string | undefined => patterns.find((entry) => entry.pattern.test(text))?.value;

function extractEntityTitle(message: string): Pick<ExtraData, "entity_title"> {
  const titleMatch = message.match(/"([^"]+)"/);
  return titleMatch ? { entity_title: titleMatch[1] } : {};
}

function extractDefectSeverity(message: string): Pick<ExtraData, "severity"> {
  const severityMatch = message.match(/\(([^)]+)\)/);
  const severityText = severityMatch?.[1].toLowerCase() ?? "";
  const severity = extractPatternValue(severityText, severityPatterns);

  return severity ? { severity } : {};
}

function extractDefectStatus(message: string): Pick<ExtraData, "status"> {
  const statusMatch = message.match(/(?:auf|to)\s+"?([^"]+)"?\s*(?:geändert|changed)?\.?$/i);
  const statusText = statusMatch?.[1].toLowerCase().trim() ?? "";
  const status = extractPatternValue(statusText, statusPatterns);

  return status ? { status } : {};
}

function extractDueDate(message: string): Pick<ExtraData, "due_date"> {
  const dateMatch = message.match(/(\d{2}\.\d{2}\.\d{4})/);
  return dateMatch ? { due_date: dateMatch[1] } : {};
}

function extractDaysOverdue(message: string): Pick<ExtraData, "days_overdue"> {
  const daysMatch = message.match(/(\d+)\s*Tag/);
  return daysMatch ? { days_overdue: parseInt(daysMatch[1], 10) } : {};
}

/**
 * Extracts entity title and extra data from a notification message (legacy fallback)
 * This is used for older notifications that don't have structured payload data
 */
function extractDataFromMessage(message: string, type: NotificationType): ExtraData {
  const typeSpecificExtractors: Partial<
    Record<NotificationType, Array<(value: string) => ExtraData>>
  > = {
    defect_created: [extractDefectSeverity],
    defect_status_changed: [extractDefectStatus],
    due_reminder: [extractDueDate],
    overdue: [extractDaysOverdue],
  };

  return [extractEntityTitle, ...(typeSpecificExtractors[type] ?? [])].reduce<ExtraData>(
    (data, extractor) => ({ ...data, ...extractor(message) }),
    {},
  );
}

interface LocalizationContext {
  t: TFunction;
  data: ExtraData;
  entityTitle: string;
  entityType: string;
  typeLabel: string;
  rawMessage: string;
}

type LocalizedNotification = { title: string; message: string };
type NotificationLocalizer = (context: LocalizationContext) => LocalizedNotification;

const taskFallback = ({ entityType, t }: LocalizationContext) =>
  entityType || t("notifications.entities.task");

const localizedSeverity = ({ data, t }: LocalizationContext): string =>
  data.severity ? t(`notifications.severities.${data.severity}`, { defaultValue: "" }) : "";

const localizedStatus = ({ data, t }: LocalizationContext): string =>
  data.status ? t(`notifications.statuses.${data.status}`, { defaultValue: "" }) : "";

const defectCreatedMessage = (context: LocalizationContext): string => {
  const { t, entityTitle } = context;
  const title = entityTitle || t("defects.title");
  const severityLabel = localizedSeverity(context);
  const severity =
    entityTitle && severityLabel ? severityLabel : t("notifications.severities.medium");

  return t("notifications.dynamicMessages.defect_created", { title, severity });
};

const defectStatusChangedMessage = (context: LocalizationContext): string => {
  const { t, entityTitle } = context;
  return t("notifications.dynamicMessages.defect_status_changed", {
    title: entityTitle || t("defects.title"),
    status: localizedStatus(context) || t("notifications.statuses.in_progress"),
  });
};

const completedInspectionTitle = ({ data, entityTitle, t }: LocalizationContext): string =>
  entityTitle && data.position_name
    ? `${entityTitle} @ ${data.position_name}`
    : entityTitle || t("inspections.title");

const dueReminderDate = ({ data, entityTitle, t }: LocalizationContext): string =>
  entityTitle && data.due_date ? data.due_date : t("common.today");

const notificationLocalizers: Record<NotificationType, NotificationLocalizer> = {
  assignment: ({ t, entityTitle, entityType, typeLabel }) => ({
    title: entityType ? t("notifications.dynamicTitles.assignment", { entityType }) : typeLabel,
    message: entityTitle
      ? t("notifications.dynamicMessages.assignment", { title: entityTitle })
      : t("notifications.dynamicMessages.assignment", {
          title: entityType || t("notifications.entities.task"),
        }),
  }),

  reassignment: (context) => {
    const { t, data, entityTitle, entityType, typeLabel } = context;
    const newAssigneeName = data.assignee_name || t("common.unknown");
    return {
      title: entityType ? t("notifications.dynamicTitles.reassignment", { entityType }) : typeLabel,
      message: entityTitle
        ? t("notifications.dynamicMessages.reassignment", { title: entityTitle, newAssigneeName })
        : t("notifications.dynamicMessages.reassignment", {
            title: taskFallback(context),
            newAssigneeName,
          }),
    };
  },

  assignment_to_created: (context) => {
    const { t, data, entityTitle, entityType, typeLabel } = context;
    const assigneeName = data.assignee_name || t("common.unknown");
    return {
      title: entityType
        ? t("notifications.dynamicTitles.assignment_to_created", { entityType })
        : typeLabel,
      message: entityTitle
        ? t("notifications.dynamicMessages.assignment_to_created", {
            title: entityTitle,
            assigneeName,
          })
        : t("notifications.dynamicMessages.assignment_to_created", {
            title: taskFallback(context),
            assigneeName,
          }),
    };
  },

  defect_created: (context) => ({
    title: context.typeLabel,
    message: defectCreatedMessage(context),
  }),

  defect_status_changed: (context) => ({
    title: context.typeLabel,
    message: defectStatusChangedMessage(context),
  }),

  inspection_completed: (context) => ({
    title: context.typeLabel,
    message: context.t("notifications.dynamicMessages.inspection_completed", {
      title: completedInspectionTitle(context),
    }),
  }),

  due_reminder: (context) => ({
    title: context.entityType
      ? context.t("notifications.dynamicTitles.due_reminder", { entityType: context.entityType })
      : context.typeLabel,
    message: context.t("notifications.dynamicMessages.due_reminder", {
      title: context.entityTitle || taskFallback(context),
      date: dueReminderDate(context),
    }),
  }),

  overdue: (context) => {
    const { t, entityTitle, entityType, typeLabel } = context;
    return {
      title: entityType ? t("notifications.dynamicTitles.overdue", { entityType }) : typeLabel,
      message: entityTitle
        ? t("notifications.dynamicMessages.overdue", { title: entityTitle })
        : t("notifications.dynamicMessages.overdue", { title: taskFallback(context) }),
    };
  },

  defect_comment: ({ t, data, entityTitle, typeLabel }) => {
    const commenterName = data.commenter_name || t("common.unknown");
    return {
      title: typeLabel,
      message: entityTitle
        ? t("notifications.dynamicMessages.defect_comment", { title: entityTitle, commenterName })
        : t("notifications.dynamicMessages.defect_comment", {
            title: t("defects.title"),
            commenterName,
          }),
    };
  },

  defect_closed: ({ t, data, entityTitle, typeLabel }) => {
    const closedByName = data.closed_by_name || data.assignee_name;
    return {
      title: typeLabel,
      message: entityTitle
        ? t("notifications.dynamicMessages.defect_closed", { title: entityTitle, closedByName })
        : t("notifications.dynamicMessages.defect_closed", {
            title: t("defects.title"),
            closedByName,
          }),
    };
  },

  // TES-2028: Eskalation (Mig 359) — Server lokalisiert pro Empfaenger, der Client
  // uebersetzt nach, sobald entity_title im Payload liegt (Sprachwechsel-sicher).
  escalation: ({ t, entityTitle, typeLabel, rawMessage }) => ({
    title: typeLabel,
    message: entityTitle
      ? t("notifications.dynamicMessages.escalation", { title: entityTitle })
      : rawMessage,
  }),

  // TES-2053: zu-verifizieren-Task (Mig 380/381) — DB-Trigger schreibt Rohtext in Empfaengersprache,
  // der Client uebersetzt nach, sobald entity_title im Payload liegt (Muster escalation).
  inspection_to_verify: ({ t, entityTitle, typeLabel, rawMessage }) => ({
    title: typeLabel,
    message: entityTitle
      ? t("notifications.dynamicMessages.inspection_to_verify", { title: entityTitle })
      : rawMessage,
  }),

  // TES-2082: Owner-Deaktivierung → Re-Staffing (Mig 388/389). DB-Trigger schreibt die Message als
  // Rohtext in Empfaengersprache (Gruppenname eingebettet, kein entity_title im Payload), der Client
  // setzt einen lokalisierten Titel (notifications.types.owner_restaffing) und zeigt die Message verbatim.
  // Muster escalation: nur wenn ausnahmsweise entity_title im Payload liegt, wird die Message
  // typ-spezifisch nachlokalisiert.
  // TES-2080 (Typ 6, Mig 393): derselbe notification_type transportiert per payload.role='items_reassignment'
  // den „N Items brauchen Neuzuweisung"-Anlass (ein Bearbeiter wurde deaktiviert, KEINE Owner-Vakanz).
  // Dann einen eigenen Titel (notifications.types.items_reassignment) rendern und die DB-Message verbatim
  // zeigen — NICHT als Owner-Nachbesetzung nachlokalisieren (der eingebettete Gruppenname darf das nicht
  // triggern). Der genuine owner_restaffing-Fall (kein Marker / role='owner_restaffing') bleibt unverändert.
  owner_restaffing: ({ t, data, entityTitle, typeLabel, rawMessage }) => {
    if (data.role === "items_reassignment") {
      return {
        title: t("notifications.types.items_reassignment"),
        message: rawMessage,
      };
    }
    return {
      title: typeLabel,
      message: entityTitle
        ? t("notifications.dynamicMessages.owner_restaffing", { title: entityTitle })
        : rawMessage,
    };
  },
};

const fallbackNotificationLocalizer: NotificationLocalizer = ({ typeLabel, rawMessage }) => ({
  title: typeLabel,
  message: rawMessage,
});

function getNotificationLocalizer(type: string): NotificationLocalizer {
  return (
    (notificationLocalizers as Partial<Record<string, NotificationLocalizer>>)[type] ||
    fallbackNotificationLocalizer
  );
}

/**
 * Get localized notification title and message based on notification type
 * Uses structured payload data when available, falls back to message parsing
 */
export function getLocalizedNotification(
  notification: NotificationData,
  t: TFunction,
): { title: string; message: string } {
  // Prefer structured payload, fall back to parsing message
  const payloadData = notification.payload || {};
  const extractedData = extractDataFromMessage(notification.message, notification.type);

  // Merge: payload takes precedence over extracted
  const data: ExtraData = {
    ...extractedData,
    ...payloadData,
  };

  const entityTitle = data.entity_title || "";

  // Get localized entity type
  const entityType = notification.entity_type
    ? t(`notifications.entities.${notification.entity_type}`, { defaultValue: "" })
    : "";

  // Get the simple type label as fallback title
  const typeLabel = t(`notifications.types.${notification.type}`, {
    defaultValue: notification.title,
  });

  return getNotificationLocalizer(notification.type)({
    t,
    data,
    entityTitle,
    entityType,
    typeLabel,
    rawMessage: notification.message,
  });
}
